// ─── src/chat/WorkspaceLeft.jsx ───────────────────────────────────────────────
import { useState } from "react";
import { useAura }      from "../stores/auraCore";
import { useShortlist } from "../shortlist/ShortlistContext";
import Icon             from "./Icons";
import SaveChatModal    from "./SaveChatModal";
import LoginGateModal  from "../components/modals/LoginGateModal";
import { YourWeddingRail, WeddingPath, WeddingMood, WeddingDirections, WeddingJourney, WeddingTimeline } from "./WorkspaceRight";
import WorkspaceCenter from "./WorkspaceCenter";

const GOLD    = "#d39a21";

// Your Wedding roster, role labels, wedding-logical order, and state styling.
const WED_ROLE_LABEL = { venue: "Venue", planner: "Planner", photographer: "Photographer", videographer: "Videographer", florist: "Florist", catering: "Food & Drink", entertainment: "Entertainment", celebrant: "Celebrant", vendor: "Supplier" };
const WED_ROLE_SEQ   = ["venue", "planner", "photographer", "videographer", "florist", "catering", "entertainment", "celebrant", "vendor"];
const WED_STATE_LABEL = { needed: "Needed", suggested: "Suggested", exploring: "Exploring", shortlisted: "Shortlisted", enquired: "Enquired", booked: "Booked" };
const WED_STATE_COLOR = { needed: "rgba(200,190,175,0.5)", suggested: "#c9a24b", exploring: "#d39a21", shortlisted: "#e0b64d", enquired: "#10b981", booked: "#22c55e" };
const wedRoleRank = (r) => { const i = WED_ROLE_SEQ.indexOf(r); return i === -1 ? WED_ROLE_SEQ.length : i; };

// ── Context-aware suggested questions ─────────────────────────────────────────
function getQuickAsks(activeContext) {
  const { page, country, region, compareVenues = [] } = activeContext || {};

  // Compare mode, questions about the specific venues being evaluated
  if (page === 'compare' && compareVenues.length > 0) {
    const names = compareVenues.map(v => v.name);
    const first = names[0];
    const second = names[1];
    return [
      second
        ? `What's the key difference between ${first} and ${second}?`
        : `Tell me more about ${first}`,
      "Which of these venues offers exclusive use?",
      "Which venue is better for an intimate wedding under 80 guests?",
      "What are the travel options for guests at each venue?",
      "Which venue has the stronger reputation for food and service?",
      "What time of year is best for each of these venues?",
    ];
  }

  // Region-specific suggestions
  if (region && region !== 'All regions') {
    return [
      `Show me the top-rated venues in ${region}`,
      `Which ${region} venues offer exclusive use?`,
      `Find photographers based near ${region}`,
      `What's the best season to wed in ${region}?`,
      "What's included in typical venue packages?",
      "Find a venue with accommodation on site",
    ];
  }

  // Country-specific suggestions
  if (country) {
    return [
      `Show me luxury venues in ${country} for 100+ guests`,
      `Which venues in ${country} have a romantic style?`,
      `Find a wedding planner based in ${country}`,
      "What venues offer exclusive hire?",
      "What's included in typical venue packages?",
      "Show me venues available for winter weddings",
    ];
  }

  // General fallback
  return [
    "Find me a luxury venue for 80 guests",
    "What venues offer full exclusive use?",
    "Show me venues with a romantic or historic style",
    "What's typically included in a venue package?",
    "Find a wedding planner in Europe",
    "Which venues are available in summer?",
  ];
}

// ── Theme helper ───────────────────────────────────────────────────────────────
function getT(dark) {
  if (dark) return {
    text:    "#FFFFFF",
    grey:    "rgba(255,255,255,0.74)",
    divider: "rgba(211,154,33,0.16)",
    rowBg:   "rgba(255,255,255,0.03)",
    btnBg:   "rgba(211,154,33,0.08)",
    btnHov:  "rgba(211,154,33,0.16)",
    iconBg:  "rgba(255,255,255,0.03)",
    iconHov: "rgba(255,255,255,0.07)",
    sectionLbl: "rgba(255,255,255,0.6)",
  };
  return {
    text:    "#1A1714",
    grey:    "rgba(26,23,20,0.75)",
    divider: "rgba(26,23,20,0.1)",
    rowBg:   "rgba(26,23,20,0.04)",
    btnBg:   "rgba(211,154,33,0.08)",
    btnHov:  "rgba(211,154,33,0.16)",
    iconBg:  "rgba(26,23,20,0.05)",
    iconHov: "rgba(26,23,20,0.1)",
    sectionLbl: "rgba(26,23,20,0.6)",
  };
}

// ── Collapsed icon rail ────────────────────────────────────────────────────────
function CollapsedRail({ onNewChat, shortlistCount, darkMode }) {
  const T = getT(darkMode);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 16, height: "100%", overflowY: "auto" }}>
      <IconPill icon={<Icon name="edit" size={15} />} label="New direction" gold onClick={onNewChat} T={T} />
      <IconPill icon={<Icon name="globe" size={15} />} label="Context" T={T} />
      <IconPill icon={<Icon name="messageCircle" size={15} />} label="Quick Asks" T={T} />
      <div style={{ position: "relative" }}>
        <IconPill icon={<Icon name="heart" size={15} />} label="Shortlist" T={T} />
        {shortlistCount > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            background: GOLD, color: "#0D0B09",
            width: 15, height: 15, borderRadius: "50%",
            fontSize: 8, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {shortlistCount > 9 ? "9+" : shortlistCount}
          </span>
        )}
      </div>
    </div>
  );
}

function IconPill({ icon, label, gold, onClick, T }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 36, height: 36, borderRadius: "50%",
        background:  hov ? (gold ? "rgba(211,154,33,0.2)" : T.iconHov) : (gold ? "rgba(211,154,33,0.08)" : T.iconBg),
        border:     `1px solid ${hov || gold ? "rgba(211,154,33,0.35)" : T.divider}`,
        color:       gold ? GOLD : (hov ? T.text : T.grey),
        cursor:      onClick ? "pointer" : "default",
        display:    "flex", alignItems: "center", justifyContent: "center",
        fontSize: 15, transition: "all 0.2s", flexShrink: 0,
      }}
    >{icon}</button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function WorkspaceLeft({ collapsed, onBack, darkMode, mode, initialDraft }) {
  const sendMessage   = useAura((s) => s.sendMessage);
  const clearHistory  = useAura((s) => s.clearHistory);
  const saveAndStartNewChat = useAura((s) => s.saveAndStartNewChat);
  const messages      = useAura((s) => s.messages);
  const activeContext = useAura((s) => s.activeContext);
  const wedding       = useAura((s) => s.wedding);
  const weddingTeam   = useAura((s) => s.weddingTeam);
  const weddingScenarios = useAura((s) => s.weddingScenarios) ?? [];
  const weddingJourney   = useAura((s) => s.weddingJourney) ?? [];
  const restoreScenario  = useAura((s) => s.restoreScenario);
  const focusEntity   = useAura((s) => s.focusEntity);
  const recItems      = useAura((s) => s.recommendations?.items) ?? [];
  const lastAura      = [...messages].reverse().find((m) => m.from === "aura");
  const discover      = { editorial: lastAura?.editorial_context || [], showcase: lastAura?.showcase_context || [], weddings: lastAura?.real_wedding_context || [] };
  const { items: shortlist }                    = useShortlist();
  const T = getT(darkMode);

  const [confirmNew,      setConfirmNew]      = useState(false);
  const [showSaveModal,   setShowSaveModal]   = useState(false);
  const [showRegister,    setShowRegister]    = useState(false);

  const handleNewChat = () => {
    if (messages.length > 1) setConfirmNew(true);
    else clearHistory();
  };
  const confirmAndClear = () => { saveAndStartNewChat(); setConfirmNew(false); };

  // ── CENTRE · the wedding as the object, width-capped and centred, never a
  //    stretched sidebar. Its chrome (new chat, save) lives in the conversation. ──
  if (mode === "canvas") {
    return (
      <div style={{ height: "100%", overflowY: "auto", display: "flex", justifyContent: "center" }}>
        <div style={{ width: 560, maxWidth: "100%", padding: "48px 40px 40px", boxSizing: "border-box" }}>
          {wedding ? (
            <>
              <YourWeddingRail wedding={wedding} team={weddingTeam} recItems={recItems} discover={discover} darkMode={darkMode} />
              {/* Living canvas, the wedding, in pictures: the team's real imagery,
                  a collage that fills as the team grows. Clicking a frame focuses
                  that entity's dossier on the right (same as the roster). */}
              <WeddingMood
                team={weddingTeam}
                darkMode={darkMode}
                onFocus={(e) => focusEntity?.({ id: e.id, name: e.name })}
              />
              {/* Living canvas, the wedding's forward motion, not just its roster.
                  "Ask Aura" sends the real query for the next open role. */}
              <WeddingPath
                wedding={wedding}
                team={weddingTeam}
                darkMode={darkMode}
                onAsk={(role) => {
                  const place = wedding?.destination ? ` in ${wedding.destination}` : "";
                  const what = { venue: "a villa or venue", planner: "a wedding planner", photographer: "a wedding photographer", videographer: "a wedding videographer", florist: "a florist", catering: "a caterer", entertainment: "a band or entertainment", celebrant: "a celebrant" }[role] || `a ${role}`;
                  sendMessage(`Help me find ${what}${place} for my wedding.`);
                }}
              />
              {/* Toward the day, the forward milestone spine, anchored to the date. */}
              <WeddingTimeline wedding={wedding} team={weddingTeam} darkMode={darkMode} />
              {/* Our Journey, the wedding's own notebook, read from the event log. */}
              <WeddingJourney journey={weddingJourney} darkMode={darkMode} />
              {/* Directions, the couple's other versions of the wedding, one tap
                  from returning. Nothing the Guardian kept is ever lost. */}
              <WeddingDirections
                directions={weddingScenarios}
                darkMode={darkMode}
                onOpen={(id) => restoreScenario?.(id)}
              />
              {/* A quiet close in Aura's voice, the canvas resolves, and reads as
                  tended rather than static. The same concierge voice as the
                  guidance line, at the foot of the wedding. */}
              <div style={{ marginTop: 34, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ marginTop: 3, flexShrink: 0 }}><Icon name="sparkleMini" size={12} color={GOLD} /></span>
                <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 12.5, color: T.grey, lineHeight: 1.5 }}>
                  Your wedding lives here. I keep it current as we talk, and everything you choose stays.
                </div>
              </div>
            </>
          ) : (
            <div style={{ paddingTop: 36 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
                <Icon name="sparkleMini" size={13} color={GOLD} />
                <span style={{ fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD }}>Your Wedding</span>
              </div>
              <div style={{ fontFamily: "var(--font-heading-primary)", fontStyle: "italic", fontSize: 16, color: T.grey, lineHeight: 1.55, maxWidth: 380 }}>
                Tell Aura about your day, and it will take shape here.
              </div>
              {/* Directions stay one tap away even on a fresh canvas, so starting a
                  new direction never strands the couple's other weddings. */}
              {weddingScenarios.length > 0 && (
                <div style={{ marginTop: 34 }}>
                  <WeddingDirections
                    directions={weddingScenarios}
                    darkMode={darkMode}
                    onOpen={(id) => restoreScenario?.(id)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LEFT · the conversation that edits the wedding, the chat, with New Chat
  //    and Save as quiet utility actions (never the main event). ──
  if (mode === "conversation") {
    if (collapsed) return <CollapsedRail onNewChat={handleNewChat} shortlistCount={shortlist.length} darkMode={darkMode} />;
    const quietBtn = {
      background: "none", border: "none", cursor: "pointer", padding: "4px 2px",
      fontFamily: "var(--font-body)", fontSize: 11, letterSpacing: "0.06em",
      color: T.grey, display: "flex", alignItems: "center", gap: 5, transition: "color 0.2s",
    };
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 16, padding: "13px 18px 9px", borderBottom: `1px solid ${T.divider}` }}>
          {!confirmNew ? (
            <button onClick={handleNewChat} style={quietBtn}
              onMouseEnter={(e) => { e.currentTarget.style.color = T.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.grey; }}>
              <Icon name="edit" size={12} /> New direction
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--font-body)", fontSize: 11.5, color: T.text }}>
              Start a new direction? This one stays in your directions.
              <button onClick={confirmAndClear} style={{ ...quietBtn, color: GOLD, padding: 0 }}>Yes</button>
              <button onClick={() => setConfirmNew(false)} style={{ ...quietBtn, padding: 0 }}>No</button>
            </div>
          )}
          {messages.length > 1 && !confirmNew && (
            <button onClick={() => setShowRegister(true)} style={{ ...quietBtn, marginLeft: "auto" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = T.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.grey; }}>Save</button>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <WorkspaceCenter darkMode={darkMode} initialDraft={initialDraft} />
        </div>
        {showSaveModal && <SaveChatModal onClose={() => setShowSaveModal(false)} />}
        {showRegister && <LoginGateModal onClose={() => setShowRegister(false)} />}
      </div>
    );
  }

  if (collapsed) {
    return <CollapsedRail onNewChat={handleNewChat} shortlistCount={shortlist.length} darkMode={darkMode} />;
  }

  return (
    <div
      style={{
        height: "100%", overflowY: "auto", display: "flex",
        flexDirection: "column", gap: 22, padding: "20px 18px 28px", boxSizing: "border-box",
      }}
    >
      {/* ── New Chat button ───────────────────────────────────────────────── */}
      {!confirmNew ? (
        <button
          onClick={handleNewChat}
          style={{
            background:     GOLD,
            border:        "none",
            color:         "#0D0B09",
            padding:       "11px 0",
            borderRadius:   6,
            fontFamily:    "var(--font-body)",
            fontSize:       11,
            fontWeight:     700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            cursor:        "pointer",
            width:         "100%",
            display:       "flex",
            alignItems:    "center",
            justifyContent: "center",
            gap:            6,
            transition:    "all 0.2s",
            flexShrink:     0,
            boxShadow:     "0 2px 8px rgba(211,154,33,0.25)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#D4B35A"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(211,154,33,0.35)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = GOLD;      e.currentTarget.style.boxShadow = "0 2px 8px rgba(211,154,33,0.25)"; }}
        >
          <Icon name="edit" size={13} /> New direction
        </button>
      ) : (
        <div style={{ background: "rgba(211,154,33,0.07)", border: `1px solid rgba(211,154,33,0.25)`, borderRadius: 6, padding: "12px 14px", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: T.text, marginBottom: 10, lineHeight: 1.45 }}>
            Start a new direction? This one stays in your directions, one tap to return.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={confirmAndClear}
              style={{ flex: 1, background: GOLD, border: "none", color: "#0D0B09", padding: "7px 0", borderRadius: 5, fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.5px" }}
            >Yes, clear</button>
            <button
              onClick={() => setConfirmNew(false)}
              style={{ flex: 1, background: "none", border: `1px solid ${T.divider}`, color: T.grey, padding: "7px 0", borderRadius: 5, fontFamily: "var(--font-body)", fontSize: 11, cursor: "pointer" }}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* ── Secondary actions (lighter, borderless) ─────────────────────── */}
      <div style={{ display: "flex", gap: 8 }}>
        {messages.length > 1 && (
          <button
            onClick={() => setShowRegister(true)}
            style={{
              background:    "none",
              border:        "1px solid rgba(211,154,33,0.35)",
              color:         GOLD,
              padding:       "5px 12px",
              fontFamily:    "var(--font-body)",
              fontSize:       11,
              fontWeight:     600,
              letterSpacing: "0.4px",
              cursor:        "pointer",
              borderRadius:  "var(--lwd-radius-input)",
              flexShrink:     0,
              transition:    "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
          >
            Save
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background:   "none",
              border:        "none",
              color:          T.grey,
              padding:       "6px 0",
              fontFamily:   "var(--font-body)",
              fontSize:       11,
              cursor:        "pointer",
              textAlign:     "left",
              flexShrink:     0,
              transition:    "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.grey; }}
          >
            Close
          </button>
        )}
      </div>

      {/* ── Your Wedding, the full living system, on the couple's side ─────── */}
      {wedding && (
        <div style={{ flexShrink: 0, marginBottom: 6 }}>
          <YourWeddingRail
            wedding={wedding}
            team={weddingTeam}
            recItems={recItems}
            discover={discover}
            darkMode={darkMode}
          />
        </div>
      )}

      {/* ── Active Context ────────────────────────────────────────────────── */}
      {activeContext && (activeContext.page === 'compare'
        ? activeContext.compareVenues?.length > 0
        : (activeContext.country || (activeContext.region && activeContext.region !== 'All regions'))
      ) && (
        <Section label="Active Context" T={T}>
          <div style={{ background: T.rowBg, border: `1px solid ${T.divider}`, borderRadius: 6, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
            {activeContext.page === 'compare' ? (
              activeContext.compareVenues.map((v) => (
                <ContextRow key={v.id} icon={<Icon name="heart" size={12} color={GOLD} />} text={v.name} T={T} />
              ))
            ) : (
              <>
                {activeContext.country && (
                  <ContextRow icon={<Icon name="globe" size={12} color={GOLD} />} text={activeContext.country} T={T} />
                )}
                {activeContext.region && activeContext.region !== 'All regions' && (
                  <ContextRow icon={<Icon name="mapPin" size={12} color={GOLD} />} text={activeContext.region} T={T} />
                )}
              </>
            )}
          </div>
        </Section>
      )}

      {/* The left panel is the couple's own space (new chat, save, login). The
          generic "Suggested" prompts and the "Discover" editorial (which is OUR
          content) have moved off it, editorial now lives on the right, in the
          wedding's contextual "Explore". */}

      {/* ── Editorial / Spotlight / Real Weddings, MOVED to the right rail ─── */}
      {(() => {
        return null;
        // eslint-disable-next-line no-unreachable
        const lastAura = messages.filter(m => m.from === "aura").at(-1);
        const editorial = lastAura?.editorial_context ?? [];
        const showcase  = lastAura?.showcase_context  ?? [];
        const weddings  = lastAura?.real_wedding_context ?? [];
        if (!editorial.length && !showcase.length && !weddings.length) return null;

        const cardLink = { display: "flex", alignItems: "center", gap: 10, textDecoration: "none", borderRadius: 6, overflow: "hidden", border: `1px solid ${T.divider}`, background: T.rowBg, padding: 0 };
        const thumb    = { width: 66, height: 52, objectFit: "cover", flexShrink: 0, display: "block" };
        const body     = { padding: "6px 10px 6px 0", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 };
        const lbl      = { fontSize: 8, fontFamily: "var(--font-body)", color: GOLD, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.75 };
        const title    = { fontSize: 11, fontFamily: "var(--font-heading-primary)", color: T.text, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };

        // Close/minimise the workspace when a discover link is clicked so the
        // user can see the newly-opened tab rather than the workspace sitting
        // in front of it on the current tab.
        const handleDiscoverClick = () => { onBack?.(); };

        return (
          <Section label="Discover" T={T}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {editorial.map(a => (
                <a key={a.article_id} href={`/lwd-edit/${a.slug}`} target="_blank" rel="noopener noreferrer" onClick={handleDiscoverClick} style={cardLink}>
                  {a.cover_image && <img src={a.cover_image} alt={a.title} style={thumb} />}
                  <div style={body}>
                    <div style={lbl}>From The Edit</div>
                    <div style={title}>{a.title}</div>
                  </div>
                </a>
              ))}
              {showcase.map(s => (
                <a key={s.showcase_id} href={`/showcase/${s.slug}`} target="_blank" rel="noopener noreferrer" onClick={handleDiscoverClick} style={cardLink}>
                  {s.cover_image && <img src={s.cover_image} alt={s.title} style={thumb} />}
                  <div style={body}>
                    <div style={lbl}>{s.badge_label || "In The Spotlight"}</div>
                    <div style={title}>{s.title}</div>
                  </div>
                </a>
              ))}
              {weddings.map(w => (
                <a key={w.feature_id} href={`/real-weddings/${w.slug}`} target="_blank" rel="noopener noreferrer" onClick={handleDiscoverClick} style={cardLink}>
                  {w.cover_image && <img src={w.cover_image} alt={w.title} style={thumb} />}
                  <div style={body}>
                    <div style={lbl}>Real Weddings</div>
                    <div style={title}>{w.title}</div>
                    {w.photographer_name && <div style={{ fontSize: 8, fontFamily: "var(--font-body)", color: T.grey, opacity: 0.6 }}>Photo: {w.photographer_name}</div>}
                  </div>
                </a>
              ))}
            </div>
          </Section>
        );
      })()}

      {/* ── Save Chat Modal ─────────────────────────────────────────────────── */}
      {showSaveModal && (
        <SaveChatModal onClose={() => setShowSaveModal(false)} />
      )}

      {/* ── Register to Save ────────────────────────────────────────────────── */}
      {showRegister && (
        <LoginGateModal onClose={() => setShowRegister(false)} />
      )}
    </div>
  );
}

// ── Tiny helpers ───────────────────────────────────────────────────────────────
function Section({ label, children, T }) {
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ fontSize: 9, letterSpacing: "2px", textTransform: "uppercase", color: T.sectionLbl, opacity: 0.65, marginBottom: 8, fontFamily: "var(--font-body)" }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ContextRow({ icon, text, dim, T }) {
  return (
    <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: dim ? T.grey : T.text, opacity: dim ? 0.55 : 1, display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      {text}
    </div>
  );
}
